import type React from "react";
import { useCallback } from "react";
import * as prod from "react/jsx-runtime";
import rehypeReact from "rehype-react";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";

/**
 * Markdownコンテンツを正規化
 * - ダブルクオーテーションで囲まれている場合は取り除く
 * - エスケープシーケンスを変換
 */
export const normalizeMarkdownContent = (content: string): string => {
  let normalized = content;
  if (normalized.startsWith('"') && normalized.endsWith('"')) {
    try {
      normalized = JSON.parse(normalized);
    } catch {
      // パースできない場合はそのまま使用
    }
  }
  return normalized.replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\t/g, "\t");
};

/**
 * Markdownコンテンツを描画（スタンドアロン関数版）
 *
 * コンポーネント外で使用する場合はこちらを使用する。
 * コンテンツの正規化を自動的に行う。
 */
export const renderMarkdownToNode = (content: string): React.ReactNode => {
  if (!content) return null;
  try {
    return unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype)
      .use(rehypeReact, prod)
      .processSync(normalizeMarkdownContent(content)).result as React.ReactNode;
  } catch {
    return content;
  }
};

/**
 * Markdown描画用共通カスタムフック
 *
 * unified (remark/rehype) パイプラインを使用してMarkdownをReactNodeに変換する。
 * コンテンツの正規化（エスケープシーケンス変換、JSON引用符除去）を自動的に行う。
 *
 * @example
 * // コンポーネント内で使用
 * const { renderMarkdown } = useMarkdownRenderer();
 * const node = renderMarkdown(markdownContent);
 */
export const useMarkdownRenderer = () => {
  const renderMarkdown = useCallback((content: string): React.ReactNode => {
    return renderMarkdownToNode(content);
  }, []);

  return { renderMarkdown, normalizeMarkdownContent };
};
